import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const stressLegendDefs: Record<string, string> = {
  'Monotony': 'How similar your daily training loads are. High monotony (>1.5) = repetitive training, higher illness risk.',
  'Strain': 'Training Strain = weekly TSS \u00d7 monotony. High strain without recovery = overtraining risk.',
};

interface StressPoint {
  week: string;
  strain: number | null;
  monotony: number | null;
  weekly_tss: number | null;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const formatDateTick = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    const day = d.getDate();
    const mon = d.toLocaleDateString('en-US', { month: 'short' });
    const yr = String(d.getFullYear()).slice(-2);
    return `${day} ${mon} '${yr}`;
  } catch {
    return dateStr;
  }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const weekLabel = formatDateTick(label);
  const monotonyEntry = payload.find((p: any) => p.dataKey === 'monotony');
  const strainEntry = payload.find((p: any) => p.dataKey === 'strain');
  const tssEntry = payload.find((p: any) => p.dataKey === 'weekly_tss');

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{weekLabel}</div>
      {monotonyEntry && monotonyEntry.value !== null && (
        <div style={{ color: '#dc2626', marginBottom: 2 }}>
          Monotony: {monotonyEntry.value.toFixed(1)}
        </div>
      )}
      {strainEntry && strainEntry.value !== null && (
        <div style={{ color: '#555', marginBottom: 2 }}>
          Strain: {Math.round(strainEntry.value)}
        </div>
      )}
      {tssEntry && tssEntry.value !== null && (
        <div style={{ color: '#555', marginBottom: 2 }}>
          Weekly TSS: {Math.round(tssEntry.value)}
        </div>
      )}
    </div>
  );
};

interface TrainingStressChartProps {
  targetUserId?: number | null;
}

const TrainingStressChart: React.FC<TrainingStressChartProps> = ({ targetUserId }) => {
  const [range, setRange] = useState<ChartRange>('90d');
  const [data, setData] = useState<StressPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (r: ChartRange) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range: r });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/analytics/training-stress?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load training stress data');
      const json = await res.json();
      setData(json.points || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    fetchData(range);
  }, [range, fetchData]);

  return (
    <div className="analytics-card" style={{ marginTop: 16 }}>
      <h3>Training Monotony &amp; Strain</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="analytics-chart-loading">Loading...</div>
      ) : data.length === 0 ? (
        <div className="analytics-chart-empty">No training stress data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 12, fill: '#999' }}
              tickFormatter={(val) => formatDateTick(val)}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: '#999' }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: '#999' }}
            />
            <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={legendTooltipFormatter(stressLegendDefs)}
            />
            <Bar
              yAxisId="left"
              dataKey="strain"
              fill="#e5e7eb"
              name="Strain"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="monotony"
              stroke="#dc2626"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
              name="Monotony"
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TrainingStressChart;
